import { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import axios from 'axios';
import { Bell, AlertTriangle, FileText, Users, CalendarOff, ArrowLeftRight, RefreshCw, Moon, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ICON_MAP = { document_expiring: AlertTriangle, leave_pending: CalendarOff, swap_pending: ArrowLeftRight, profile_incomplete: Users };
const COLOR_MAP = { warning: 'text-amber-400 bg-amber-500/10', info: 'text-[#4A90E2] bg-[#4A90E2]/10', error: 'text-red-400 bg-red-500/10' };

export default function AdminNotifications() {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [quietHours, setQuietHours] = useState({ enabled: false, start: '20:00', end: '07:00' });
  const [savingQH, setSavingQH] = useState(false);
  const [overrides, setOverrides] = useState([]);
  const navigate = useNavigate();

  useEffect(() => { fetchNotifs(); fetchQuietHours(); }, []);

  const fetchNotifs = async () => {
    setLoading(true);
    try { const { data } = await axios.get(`${API_URL}/api/admin/notifications`, { withCredentials: true }); setNotifications(data); }
    catch { toast.error('Failed to load notifications'); }
    setLoading(false);
  };

  const fetchQuietHours = async () => {
    try {
      const [qhRes, ovRes] = await Promise.all([
        axios.get(`${API_URL}/api/org/quiet-hours`, { withCredentials: true }),
        axios.get(`${API_URL}/api/org/quiet-hours/overrides`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setQuietHours({ enabled: qhRes.data.enabled, start: qhRes.data.start, end: qhRes.data.end });
      setOverrides(ovRes.data || []);
    } catch { /* default values */ }
  };

  const saveQuietHours = async () => {
    setSavingQH(true);
    try {
      await axios.put(`${API_URL}/api/org/quiet-hours`, quietHours, { withCredentials: true });
      toast.success('Quiet hours updated');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update'); }
    setSavingQH(false);
  };

  const handleClick = (n) => {
    if (n.type === 'leave_pending') navigate('/leave-requests');
    else if (n.type === 'swap_pending') navigate('/shift-swaps');
    else if (n.type === 'profile_incomplete') navigate('/dashboard/admin/caregivers');
    else if (n.type === 'document_expiring' && n.caregiver_id) navigate('/dashboard/admin/caregivers');
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="admin-notifications-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl sm:text-3xl font-heading font-bold text-foreground">Notifications</h1>
            <p className="text-muted-foreground mt-1">{notifications.length} notification(s)</p>
          </div>
          <Button variant="outline" onClick={fetchNotifs} className="border-border text-foreground gap-2"><RefreshCw className="w-4 h-4" />Refresh</Button>
        </div>

        {/* Quiet Hours Configuration */}
        <Card className="glass-card border-border" data-testid="quiet-hours-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base text-foreground flex items-center gap-2">
              <Moon className="w-5 h-5 text-[#8B5CF6]" />Quiet Hours
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-xs text-muted-foreground">
              Non-urgent push notifications are suppressed during quiet hours. Emergency and SOS alerts always go through.
            </p>
            <div className="flex items-center justify-between p-3 bg-muted rounded-xl border border-border">
              <div>
                <p className="text-sm text-foreground font-medium">Enable Quiet Hours</p>
                <p className="text-xs text-muted-foreground">Suppress non-urgent alerts outside working hours</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer" data-testid="quiet-hours-toggle">
                <input type="checkbox" checked={quietHours.enabled}
                  onChange={e => setQuietHours(q => ({ ...q, enabled: e.target.checked }))}
                  className="sr-only peer" />
                <div className="w-10 h-5 bg-muted peer-checked:bg-[#8B5CF6] rounded-full transition-colors after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:w-4 after:h-4 after:bg-white after:rounded-full after:transition-transform peer-checked:after:translate-x-5" />
              </label>
            </div>
            {quietHours.enabled && (
              <div className="flex items-center gap-3 flex-wrap">
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-muted-foreground" />
                  <span className="text-xs text-muted-foreground">From</span>
                  <input type="time" value={quietHours.start}
                    onChange={e => setQuietHours(q => ({ ...q, start: e.target.value }))}
                    className="rounded-lg border border-border bg-muted px-3 py-1.5 text-sm text-foreground"
                    data-testid="quiet-hours-start" />
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground">To</span>
                  <input type="time" value={quietHours.end}
                    onChange={e => setQuietHours(q => ({ ...q, end: e.target.value }))}
                    className="rounded-lg border border-border bg-muted px-3 py-1.5 text-sm text-foreground"
                    data-testid="quiet-hours-end" />
                </div>
              </div>
            )}
            <Button className="btn-primary text-sm" onClick={saveQuietHours} disabled={savingQH} data-testid="save-quiet-hours-btn">
              {savingQH ? 'Saving...' : 'Save Quiet Hours'}
            </Button>
            {/* User Overrides */}
            {overrides.length > 0 && (
              <div className="pt-3 border-t border-border/40" data-testid="overrides-list">
                <p className="text-xs text-muted-foreground font-medium mb-2">Staff Overrides ({overrides.length})</p>
                <div className="space-y-1.5 max-h-40 overflow-y-auto">
                  {overrides.map(o => (
                    <div key={o.user_id} className="flex items-center justify-between p-2 rounded-lg bg-background/50 border border-border text-xs">
                      <div>
                        <span className="text-foreground font-medium">{o.user_name}</span>
                        <span className="text-muted-foreground ml-1.5">({o.user_role})</span>
                      </div>
                      <span className={`px-1.5 py-0.5 rounded-full ${o.opt_out ? 'bg-amber-500/10 text-amber-400' : 'bg-[#8B5CF6]/10 text-[#8B5CF6]'}`}>
                        {o.opt_out ? 'Opted out' : `${o.custom_start || '—'}–${o.custom_end || '—'}`}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
        {loading ? (
          <div className="flex justify-center py-12"><div className="spinner w-8 h-8" /></div>
        ) : notifications.length === 0 ? (
          <div className="text-center py-16 glass-card rounded-xl border border-border">
            <Bell className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-30" />
            <p className="text-muted-foreground text-lg">All clear!</p>
            <p className="text-sm text-muted-foreground mt-1">No notifications at this time</p>
          </div>
        ) : (
          <div className="space-y-3">
            {notifications.map((n, i) => {
              const Icon = ICON_MAP[n.type] || Bell;
              const colors = COLOR_MAP[n.severity] || COLOR_MAP.info;
              return (
                <Card key={i} className="glass-card border-border card-hover cursor-pointer" onClick={() => handleClick(n)} data-testid={`notification-${i}`}>
                  <CardContent className="p-4 flex items-start gap-4">
                    <div className={`p-3 rounded-xl ${colors.split(' ')[1]}`}><Icon className={`w-5 h-5 ${colors.split(' ')[0]}`} /></div>
                    <div className="flex-1 min-w-0">
                      <p className="text-foreground font-medium">{n.title}</p>
                      <p className="text-sm text-muted-foreground mt-0.5">{n.message}</p>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
